import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { ApiKeyDisplay } from './models/ApiKey.interface';
import { DeleteKeyInput } from './models/DeleteKeyInput.interface';
import { GenerateKeyInput } from './models/GenerateKey.interface';
import { RenewKeyInput } from './models/RenewKeyInput.interface';
import apiKeyService from './services/apiKey.service';

interface CreateKeyState extends AsyncState {
    apiKey?: string | null;
    expiration?: string | null;
}
interface DeleteKeyState extends AsyncState { }

interface ListKeyState extends AsyncState {
    apiKeys: ApiKeyDisplay[];
}

export interface ApikeyState {
    createdKey: CreateKeyState;
    deletedKey: DeleteKeyState;
    list: ListKeyState;
}


export const generateKey = createAsyncThunk(
    'apiKey/generateKey',
    async (userInput: GenerateKeyInput, thunkApi) => {
        try {
            return await apiKeyService.generateKey(userInput);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const deleteKey = createAsyncThunk(
    'apiKey/deleteKey',
    async (userInput: DeleteKeyInput, thunkApi) => {
        try {
            return await apiKeyService.deleteKey(userInput);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const renewKey = createAsyncThunk(
    'apiKey/renewKey',
    async (userInput: RenewKeyInput, thunkApi) => {
        try {
            return await apiKeyService.renewKey(userInput);
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const listApiKeys = createAsyncThunk(
    'apiKey/listApiKeys',
    async (_, thunkApi) => {
        try {
            return await apiKeyService.listApiKeys();
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const initialState: ApikeyState = {
    createdKey: {
        apiKey: null,
        expiration: null,
        isLoading: false,
        isSuccess: false,
        isError: false,
        error: undefined,
    },
    deletedKey: {
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    list: {
        apiKeys: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
}

export const apiKeySlice = createSlice({
    name: 'apiKey',
    initialState,
    reducers: {
        resetDeletedKey: (state) => {
            state.deletedKey = {
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            };
        },
        resetApiKeys: (state) => {
            state.list = {
                apiKeys: state.list.apiKeys,
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            };
        },
        resetCreatedKey: (state) => {
            state.createdKey = {
                isLoading: false,
                isSuccess: false,
                isError: false,
                error: undefined,
            };
        }
    },
    extraReducers: (builder) => {
        builder
            // GENERATE KEY
            .addCase(generateKey.pending, (state) => {
                state.createdKey.isLoading = true;
                state.createdKey.isError = false;
                state.createdKey.error = undefined;
            })
            .addCase(generateKey.fulfilled, (state, action) => {
                state.createdKey.isLoading = false;
                state.createdKey.isSuccess = true;
                state.createdKey.apiKey = action.payload.apiKey;
                state.createdKey.expiration = action.payload.expiration;
            })
            .addCase(generateKey.rejected, (state, action) => {
                state.createdKey.isLoading = false;
                state.createdKey.isError = true;
                state.createdKey.apiKey = null;
                state.createdKey.error = action.payload as SerializedError;
            })
            // DELETE KEY
            .addCase(deleteKey.pending, (state, action) => {
                state.deletedKey.isLoading = true;
                state.deletedKey.isSuccess = false;
                state.deletedKey.isError = false;
                state.deletedKey.error = undefined;
            })
            .addCase(deleteKey.fulfilled, (state, action) => {
                state.deletedKey.isLoading = false;
                state.deletedKey.isSuccess = true;
                state.deletedKey.isError = false;
            })
            .addCase(deleteKey.rejected, (state, action) => {
                state.deletedKey.isLoading = false;
                state.deletedKey.isSuccess = false;
                state.deletedKey.isError = true;
                state.deletedKey.error = action.payload as SerializedError;
            })
            // RENEW KEY
            .addCase(renewKey.pending, (state) => {
                state.createdKey.isLoading = true;
                state.createdKey.isSuccess = false;
                state.createdKey.isError = false;
                state.createdKey.error = undefined;
            })
            .addCase(renewKey.fulfilled, (state, action) => {
                state.createdKey.isLoading = false;
                state.createdKey.isSuccess = true;
                state.createdKey.apiKey = action.payload.apiKey;
                state.createdKey.expiration = action.payload.expiration;
            })
            .addCase(renewKey.rejected, (state, action) => {
                state.createdKey.isLoading = false;
                state.createdKey.isSuccess = false;
                state.createdKey.isError = true;
                state.createdKey.error = action.payload as SerializedError;
            })
            // LIST KEYS
            .addCase(listApiKeys.pending, (state) => {
                state.list.isLoading = true;
                state.list.isSuccess = false;
                state.list.isError = false;
                state.list.error = undefined;
            })
            .addCase(listApiKeys.fulfilled, (state, action) => {
                state.list.isSuccess = true;
                state.list.isLoading = false;
                state.list.isError = false;
                state.list.error = undefined;
                state.list.apiKeys = action.payload.apiKeys;
            })
            .addCase(listApiKeys.rejected, (state, action) => {
                state.list.isSuccess = false;
                state.list.isLoading = false;
                state.list.isError = true;
                state.list.apiKeys = [];
                state.list.error = action.payload as SerializedError;
            })
    }
});


export const { resetApiKeys, resetCreatedKey, resetDeletedKey } = apiKeySlice.actions;

export const apiKeyState = (state: RootState) => state.apiKey;

export default apiKeySlice.reducer;